/* eslint-disable no-undefined */
import Button from "../../../components/Button";
import useStyles from "../styles";
import { useUserState } from "../../../context/UserContext";
import logo from "../../../assets/images/logo.svg";
import { toast } from "react-toastify";
import InputGroup from "../../../components/FormComponent/InputGroup";
import {
  Grid,
  Paper
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { httprequest } from "../../../data/api";
import { useEffect, useState } from "react";
import Modal from "../../../components/Modal";
import PageLoader from "../../../components/PageLoader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Icon } from "@iconify/react";

const SUBSCRIPTION_INVOICES = "/api/v1/subscription/invoices";

const PayInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const { appLogo } = useUserState();
  const [cancelModal, setCancelModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [invoice, setInvoice] = useState({});
  const [isDirecting, setIsRedirecting] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [payAmount, setPayAmount] = useState({
    amount: 0
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPayAmount((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const closeHandlePaymentModal = () => {
    setPaymentModal(false);
    setPayAmount({
      amount: ""
    });
  };

  /**
   * Fetches the payment URL for the invoice
   * @function getPaymentURL
   * @async
   * @description Initiates payment process and redirects to payment gateway
   */
  const getPaymentURL = () => {
    setIsRedirecting(true);
    const payload = {
      callback_url: `${process.env.REACT_APP_BASE_URL}/auth/login`
    };
    httprequest
      .post(`${SUBSCRIPTION_INVOICES}/${id}/pay`, payload)
      .then((res) => {
        if (res?.data?.data?.redirect_url) {
          window.location.href = res?.data?.data?.redirect_url;
          toast.success(res?.data?.data?.message);
        } else {
          toast.error(res?.data?.message || "Failed to get payment link");
        }
        setIsRedirecting(false);
      }).catch(() => {
        setIsRedirecting(false);
      });
  };

  /**
   * Fetches the status of a specific invoice
   * @function getViewInvoiceStatus
   * @async
   * @description Retrieves invoice details from the server and updates state
   */
  const getViewInvoiceStatus = () => {
    setIsLoading(true);
    httprequest
      .get(`${SUBSCRIPTION_INVOICES}/${id}`)
      .then((res) => {
        setInvoice(res?.data?.data);
        setIsLoading(false);
      }).catch((res) => {
        setIsLoading(false);
        toast.error(res?.data?.response?.message || "Failed to get invoice details");
      });
  };

  /**
   * Closes the cancel modal and navigates back to invoices list
   * @function handleCloseCancelModal
   * @description Handles modal closure and navigation
   */
  const handleCloseCancelModal = () => {
    setCancelModal(false);
    navigate("/payment/invoice");
  };

  const DetailItem = ({ label, value }) => {
    const renderValue = () => {
      if (value === null || value === undefined) return "--/--";

      if (typeof value === "object" && value !== null) {
        return value.name || value.value || "--/--";
      }

      return value;
    };

    return (
      <div className={classes.detailItem}>
        <label>{label}</label>
        <p>{renderValue()}</p>
      </div>
    );
  };

  /**
   * Format date for display
   * @function formatDate
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    if (!dateString) return "--/--";
    const date = new Date(dateString);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  /**
   * Format currency for display
   * @function formatCurrency
   * @param {number} amount - Amount to format
   * @returns {string} Formatted currency string
   */
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "0";

    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: invoice.currency || "NGN"
    }).format(amount);
  };

  /**
   * Calculate pricing summary from invoice data
   * @function calculatePricingSummary
   * @returns {Object} Pricing summary object
   */
  const calculatePricingSummary = () => {
    if (!invoice.metadata) return null;

    const usedDays = invoice.metadata.used_days || 0;
    const dailyPrice = invoice.metadata.daily_price || 0;
    const planPrice = invoice.pricing_plan?.price || 0;
    const amountUsed = invoice.metadata.amount_used || 0;
    const amountDue = invoice.metadata.amount_due || 0;

    // Calculate days in period
    const periodStart = new Date(invoice.period_start);
    const periodEnd = new Date(invoice.period_end);
    const totalDays = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24));
    const remainingDays = totalDays - usedDays;

    return {
      usedDays,
      dailyPrice,
      planPrice,
      amountUsed,
      amountDue,
      totalDays,
      remainingDays,
      currentDaily: dailyPrice,
      newDaily: dailyPrice,
      daysUsed: usedDays,
      amountDueDisplay: amountDue,
      nextRenewal: planPrice
    };
  };

  const pricingSummary = calculatePricingSummary();

  /**
   * Fetches invoice status on component mount
   * @effect
   * @listens Mount
   * @description Initial data loading when component is mounted
   */
  useEffect(() => {
    getViewInvoiceStatus();
  }, []);

  const downloadTransactionReceipt = async () => {
    if (!invoice) {
      toast.error("No transaction data available");

      return;
    }

    try {
      const receiptContainer = document.createElement("div");
      receiptContainer.style.padding = "20px";
      receiptContainer.style.backgroundColor = "white";
      receiptContainer.style.position = "absolute";
      receiptContainer.style.left = "-9999px";
      receiptContainer.style.top = "0";

      const originalContainer = document.querySelector(".transaction-details-container");
      const clonedContent = originalContainer.cloneNode(true);

      const logoSection = document.createElement("div");
      logoSection.style.textAlign = "center";
      logoSection.style.marginBottom = "20px";
      logoSection.style.borderBottom = "2px solid #f0f0f0";
      logoSection.style.paddingBottom = "20px";

      const receiptTitle = document.createElement("h1");
      receiptTitle.textContent = "Invoice Receipt";
      receiptTitle.style.margin = "0 0 15px 0";
      receiptTitle.style.color = "#1976d2";
      receiptTitle.style.fontSize = "24px";
      receiptTitle.style.fontWeight = "bold";

      logoSection.appendChild(receiptTitle);
      receiptContainer.appendChild(logoSection);
      receiptContainer.appendChild(clonedContent);

      document.body.appendChild(receiptContainer);

      const canvas = await html2canvas(receiptContainer, {
        scale: 1.5,
        useCORS: true,
        logging: false
      });

      document.body.removeChild(receiptContainer);

      const imgData = canvas.toDataURL("image/png");
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);

      const fileName = `invoice-${invoice.id}.pdf`;
      pdf.save(fileName);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate invoice");
    }
  };

  return (
    <div className={classes.invoicemain}>
      <div className={classes.marginitem}>
        <div className="page-content padding-equal">
          {isLoading ? <PageLoader /> : (
            <Grid container spacing={3}>
              <Grid item lg={8} md={8} sm={12} xs={12}>
                <div className={`${classes.previewheader}${classes.fullwidth} margin-bottom-30 transaction-details-container`}>
                  <Paper elevation={0} className={classes.invoiceContainer}>
                    <header className={classes.invoiceheader}>
                      <div className={classes.headerLeft}>
                        <div className={classes.logoContent}>
                          <img
                            src={appLogo || logo}
                            alt="App Logo"
                            className={classes.logoContent}
                          />
                        </div>
                        <h1>Invoice Details</h1>
                        <p>See all information in your Invoice</p>
                      </div>
                      <div className={classes.headerRight}>
                        <span
                          className={classes.statusBadge}
                          style={{
                            backgroundColor:
                              invoice.status === "paid" ? "#d4edda" :
                                invoice.status === "partly_paid" ? "#fff3cd" :
                                  "#f8d7da",
                            color:
                              invoice.status === "paid" ? "#155724" :
                                invoice.status === "partly_paid" ? "#856404" :
                                  "#d9534f"
                          }}
                        >
                          {invoice.status ? invoice.status : "--/--"}
                        </span>
                        <div className={classes.invoiceNumber}>
                          <small>Invoice ID</small>
                          <span>{invoice.id || "--/--"}</span>
                        </div>
                      </div>
                    </header>

                    <Grid container spacing={3} className={classes.detailsGrid}>
                      <Grid item xs={12} sm={6} md={3}>
                        <DetailItem label="School" value={invoice.school_name} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DetailItem label="Plan" value={invoice.pricing_plan?.name} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DetailItem label="Period Start" value={formatDate(invoice.period_start)} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <DetailItem label="Period End" value={formatDate(invoice.period_end)} />
                      </Grid>
                    </Grid>

                    <section className={classes.invoiceParties}>
                      <div className={classes.clientInfo}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>School Information</h3>
                        <p>
                          <strong>{invoice.school_name || "--/--"}</strong>
                        </p>
                        <p>{invoice.email || "--/--"}</p>
                      </div>
                      <div className={classes.companyInfo}>
                        <h3 style={{ margin: 0, fontSize: "18px" }}>Subscription Details</h3>
                        <p>Plan: {invoice.pricing_plan?.name || "--/--"}</p>
                        <p>Price: {formatCurrency(invoice.pricing_plan?.price)}</p>
                      </div>
                    </section>

                    <section className={classes.invoiceTableSection}>
                      <table className={classes.invoiceTable}>
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Used Days</th>
                            <th>Daily Price</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.metadata ? (
                            <tr>
                              <td>Subscription Usage ({invoice.metadata.used_days} days)</td>
                              <td>{invoice.metadata.used_days || 0}</td>
                              <td>{formatCurrency(invoice.metadata.daily_price)}</td>
                              <td>{formatCurrency(invoice.metadata.amount_used)}</td>
                            </tr>
                          ) : (
                            <tr>
                              <td colSpan="4" style={{ textAlign: "center" }}>No usage data available</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </section>

                    <section className={classes.summaryContainer}>
                      <div className={classes.summaryWrapper}>
                        <div className={classes.summaryRow}>
                          <span>PLAN PRICE</span>
                          <span>{formatCurrency(invoice.pricing_plan?.price)}</span>
                        </div>
                        <div className={classes.summaryRow}>
                          <span>USED AMOUNT</span>
                          <span>{formatCurrency(invoice.metadata?.amount_used)}</span>
                        </div>
                        <div className={classes.summaryRow}>
                          <span>AMOUNT DUE</span>
                          <span>{formatCurrency(invoice.metadata?.amount_due)}</span>
                        </div>
                        <div className={`${classes.summaryRow} ${classes.grandTotal}`}>
                          <span>TOTAL AMOUNT</span>
                          <span>{formatCurrency(invoice.metadata?.amount_due)}</span>
                        </div>
                      </div>
                    </section>

                    <footer className={classes.invoicefooter}>
                      <button className={classes.printButton} onClick={downloadTransactionReceipt}>
                        <Icon icon="mdi-light:printer" width="24" height="24" />
                        <span>Print Invoice</span>
                      </button>
                    </footer>
                  </Paper>
                  <div className={classes.paymentButton}>
                    {invoice?.status === "unpaid" && (
                      <Button
                        variant="primary"
                        buttonSize="medium"
                        color="btndefault"
                        buttonName="Make Payment"
                        onClick={getPaymentURL}
                      />
                    )}
                  </div>
                </div>
              </Grid>

              {/* Subscription Summary Section */}
              <Grid item lg={4} md={4} sm={12} xs={12}>
                <div className={classes.leftItem}>
                  <div className={classes.leftheader}>
                    <p className={classes.subTitle}>Subscription Summary</p>
                  </div>
                  <div className={classes.pricingBox}>
                    <div className={classes.pricesinfo}>
                      <p>Current Plan</p>
                      <h3>{invoice.pricing_plan?.name || "--/--"}</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <p>Plan Price</p>
                      <h3>{formatCurrency(invoice.pricing_plan?.price)}</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <p>Used Days</p>
                      <h3>{pricingSummary?.usedDays ?? "--"} days</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <p>Remaining Days</p>
                      <h3>{pricingSummary?.remainingDays ?? "--"} days</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <p>Daily Price</p>
                      <h3>{formatCurrency(invoice.metadata?.daily_price)}</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <p>Amount Used</p>
                      <h3>{formatCurrency(invoice.metadata?.amount_used)}</h3>
                    </div>
                    <div className={classes.pricesinfo}>
                      <h4>Amount due now</h4>
                      <h4>{formatCurrency(invoice.metadata?.amount_due)}</h4>
                    </div>
                    <div className={classes.pricesinfo}>
                      <h4>Amount on next renewal</h4>
                      <h4>{formatCurrency(invoice.pricing_plan?.price)}</h4>
                    </div>
                  </div>
                </div>

                <div className={classes.chargesBox}>
                  <p>
                    <strong>How your charges are calculated:</strong>
                  </p>

                  {pricingSummary ? (
                    <ul>
                      <li>
                        Daily price of your plan — {formatCurrency(pricingSummary.dailyPrice)}/day
                      </li>
                      <li>
                        Amount used — {formatCurrency(pricingSummary.dailyPrice)} × {pricingSummary.usedDays} day{pricingSummary.usedDays !== 1 ? "s" : ""} ={" "}
                        {formatCurrency(pricingSummary.amountUsed)}
                      </li>
                      <li>
                        Total period — {pricingSummary.totalDays} day{pricingSummary.totalDays !== 1 ? "s" : ""}
                      </li>
                      <li>
                        Remaining days — {pricingSummary.remainingDays} day{pricingSummary.remainingDays !== 1 ? "s" : ""}
                      </li>
                      <li>
                        Amount due — {formatCurrency(pricingSummary.amountDue)}
                      </li>
                    </ul>
                  ) : (
                    <p className={classes.muted}>No pricing data available.</p>
                  )}
                </div>
              </Grid>
            </Grid>
          )}
        </div>

        <Modal
          title="Cancel"
          subtitle={
            <div>
              Are you sure you want to cancel?
            </div>
          }
          modalContent={<></>}
          modalFooter={
            <>
              <div className="flex gap-10">
                <Button
                  variant="dangeroutline"
                  buttonSize="full"
                  color="btndanger"
                  buttonName="Go Back"
                  onClick={handleCloseCancelModal}
                />
                <Button
                  variant="danger"
                  buttonName="Yes, Cancel"
                  buttonSize="full"
                  color="btndefault"
                  onClick={() => navigate("/payment/overview")}
                />
              </div>
            </>
          }
          isopen={cancelModal}
          closeModal={handleCloseCancelModal}
        />

        {/* Payment Modal */}
        <Modal
          title="Make Payment"
          subtitle={
            <div>
              Enter the amount you want to pay for this invoice.
            </div>
          }
          modalContent={
            <>
              <Grid container spacing={2} className="input-padding">
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <InputGroup
                    label="Amount to Pay"
                    margin="normal"
                    placeholder={`Enter Amount (Max: ${formatCurrency(invoice.metadata?.amount_due)})`}
                    inputType="number"
                    inputName="amount"
                    value={payAmount.amount}
                    onChange={handleChange}
                    max={invoice.metadata?.amount_due}
                  />
                </Grid>
              </Grid>
            </>
          }
          modalFooter={
            <>
              <Button
                variant="primary"
                buttonName={isDirecting ? "Redirecting..." : "Proceed to Pay"}
                buttonSize="full"
                color="btndefault"
                onClick={getPaymentURL}
                disabled={!payAmount.amount || payAmount.amount <= 0 || payAmount.amount > invoice.metadata?.amount_due}
              />
            </>
          }
          isopen={paymentModal}
          closeModal={closeHandlePaymentModal}
        />
      </div>
      {/* <ToastContainer /> */}
    </div>
  );
};

export default PayInvoice;
